/**
 * Chocowild commerce interactions.
 * All controls retain native form and link fallbacks when JavaScript is unavailable.
 */
(() => {
  const SELECTORS = {
    product: "[data-cw-product]",
    galleryThumb: "[data-gallery-thumb]",
    gallerySlide: "[data-gallery-slide]",
    quantity: "[data-quantity]",
    quickAdd: "[data-cw-quick-add]",
    collection: "[data-cw-collection]",
    search: "[data-cw-search-page]",
    cart: "[data-cw-cart]",
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || "USD";

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  /* --------------------------------------------------------------------------
   * Quantity controls
   * -------------------------------------------------------------------------- */

  const initQuantities = (scope = document) => {
    scope.querySelectorAll(SELECTORS.quantity).forEach((quantity) => {
      if (quantity.dataset.initialized === "true") return;

      const input = quantity.querySelector('input[type="number"]');
      const minus = quantity.querySelector("[data-quantity-minus]");
      const plus = quantity.querySelector("[data-quantity-plus]");

      if (!input || !minus || !plus) return;

      const changeValue = (change) => {
        const minimum = Number(input.min || 0);
        const maximum = Number(input.max || Infinity);
        const nextValue = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + change));

        input.value = String(nextValue);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      };

      minus.addEventListener("click", () => changeValue(-1));
      plus.addEventListener("click", () => changeValue(1));
      quantity.dataset.initialized = "true";
    });
  };

  /* --------------------------------------------------------------------------
   * Product gallery and variant selection
   * -------------------------------------------------------------------------- */

  const activateGalleryMedia = (productRoot, mediaId) => {
    if (!mediaId) return;

    productRoot.querySelectorAll(SELECTORS.gallerySlide).forEach((slide) => {
      const isActive = slide.dataset.mediaId === String(mediaId);
      slide.hidden = !isActive;
      slide.classList.toggle("is-hidden", !isActive);

      if (!isActive) {
        slide.querySelectorAll("video").forEach((video) => video.pause());
      }
    });

    productRoot.querySelectorAll(SELECTORS.galleryThumb).forEach((thumb) => {
      const isActive = thumb.dataset.targetMedia === String(mediaId);
      thumb.classList.toggle("is-active", isActive);
      thumb.setAttribute("aria-selected", String(isActive));
    });
  };

  const updateInventoryStatus = (productRoot, variant) => {
    const statusContainer = productRoot.querySelector("[data-inventory-status]");
    if (!statusContainer) return;

    if (!variant || !variant.available) {
      statusContainer.innerHTML = `<span class="cw-inventory-badge cw-inventory-badge--out-of-stock">Out of stock</span>`;
      return;
    }

    if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 10) {
      statusContainer.innerHTML = `<span class="cw-inventory-badge cw-inventory-badge--low">Low stock: ${variant.inventory_quantity} left</span>`;
    } else {
      statusContainer.innerHTML = `<span class="cw-inventory-badge cw-inventory-badge--in-stock">In stock</span>`;
    }
  };

  const updateSku = (productRoot, variant) => {
    const skuContainer = productRoot.querySelector("[data-sku]");
    if (!skuContainer) return;

    if (variant && variant.sku) {
      const skuValue = skuContainer.querySelector("[data-sku-value]") || skuContainer;
      skuValue.textContent = variant.sku;
      skuContainer.classList.remove("is-hidden");
      skuContainer.hidden = false;
    } else {
      skuContainer.classList.add("is-hidden");
      skuContainer.hidden = true;
    }
  };

  const initProduct = (productRoot) => {
    if (productRoot.dataset.initialized === "true") return;

    const form = productRoot.querySelector("[data-product-form]");
    const variantInput = productRoot.querySelector("[data-variant-id]");
    const radioOptionInputs = [...productRoot.querySelectorAll(".cw-product-option__input")];
    const selectOptionInputs = [...productRoot.querySelectorAll("[data-option-select]")];
    const submitButton = productRoot.querySelector("[data-add-to-cart]");
    const submitText = productRoot.querySelector("[data-add-to-cart-text]");
    const submitPrice = productRoot.querySelector("[data-add-to-cart-price]");
    const price = productRoot.querySelector("[data-price]");
    const comparePrice = productRoot.querySelector("[data-compare-price]");
    const quantityInput = productRoot.querySelector(
      ".cw-product-quantity-block input[type=number]"
    );

    let variants = [];
    let activeVariant = null;

    try {
      variants = JSON.parse(productRoot.dataset.productVariants || "[]");
    } catch (_error) {
      variants = [];
    }

    productRoot.querySelectorAll(SELECTORS.galleryThumb).forEach((thumb) => {
      thumb.addEventListener("click", () => activateGalleryMedia(productRoot, thumb.dataset.targetMedia));
    });

    const selectedOptions = () => {
      if (selectOptionInputs.length > 0) {
        return selectOptionInputs.map((select) => select.value);
      }
      return [...productRoot.querySelectorAll(".cw-product-option")].map(
        (group) => group.querySelector("input:checked")?.value
      );
    };

    const getQuantity = () => {
      const minimum = Number(quantityInput?.min || 1);
      const maximum = Number(quantityInput?.max || Infinity);
      const parsed = Number(quantityInput?.value);

      if (!Number.isFinite(parsed)) return minimum;
      return Math.min(maximum, Math.max(minimum, Math.floor(parsed)));
    };

    const updateSubmitPrice = () => {
      if (!submitPrice) return;
      submitPrice.textContent =
        activeVariant?.available
          ? ` · ${formatMoney(activeVariant.price * getQuantity())}`
          : "";
    };

    const updateVariant = (updateUrl = true) => {
      const options = selectedOptions();
      const variant =
        options.length === 0
          ? variants.find((candidate) => String(candidate.id) === variantInput?.value) || variants[0]
          : variants.find(
              (candidate) =>
                candidate.options.length === options.length &&
                candidate.options.every((option, index) => option === options[index])
            );

      productRoot.querySelectorAll(".cw-product-option").forEach((group) => {
        const label = group.querySelector("[data-option-label]");
        const checked = group.querySelector("input:checked");
        if (label && checked) label.textContent = checked.value;
      });

      if (!variant) {
        activeVariant = null;
        if (submitButton) submitButton.disabled = true;
        if (submitText) submitText.textContent = window.cwI18n?.("unavailable") || "Unavailable";
        updateSubmitPrice();
        updateInventoryStatus(productRoot, null);
        updateSku(productRoot, null);
        return;
      }

      activeVariant = variant;
      if (variantInput) variantInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price);

      if (comparePrice) {
        const isDiscounted = variant.compare_at_price && variant.compare_at_price > variant.price;
        comparePrice.hidden = !isDiscounted;
        comparePrice.textContent = isDiscounted ? formatMoney(variant.compare_at_price) : "";
      }

      if (submitButton) submitButton.disabled = !variant.available;
      if (submitText) {
        submitText.textContent = variant.available
          ? window.cwI18n?.("add_to_bag") || "Add to bag"
          : window.cwI18n?.("sold_out") || "Sold out";
      }
      updateSubmitPrice();

      updateInventoryStatus(productRoot, variant);
      updateSku(productRoot, variant);
      activateGalleryMedia(productRoot, variant.featured_media?.id);

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }
    };

    radioOptionInputs.forEach((input) => input.addEventListener("change", () => updateVariant(true)));
    selectOptionInputs.forEach((select) => select.addEventListener("change", () => updateVariant(true)));
    quantityInput?.addEventListener("input", updateSubmitPrice);
    quantityInput?.addEventListener("change", updateSubmitPrice);
    updateVariant(false);

    if (form) {
      form.addEventListener("submit", (event) => submitProductForm(event, form));
    }

    productRoot.dataset.initialized = "true";
  };

  /* --------------------------------------------------------------------------
   * AJAX add to bag with drawer cart sync and header notification
   * -------------------------------------------------------------------------- */

  const refreshCartIndicator = async () => {
    const root = window.Shopify?.routes?.root || "/";
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return;

    const cart = await response.json();
    const cartLink = document.querySelector('.cw-header__actions a[href*="/cart"]');
    let count = cartLink?.querySelector(".cw-cart-count");

    if (cartLink) {
      cartLink.setAttribute(
        "aria-label",
        window.cwI18n?.("cart_label", { count: cart.item_count }) || `Cart, ${cart.item_count} items`
      );
    }

    if (cart.item_count > 0 && cartLink && !count) {
      count = document.createElement("span");
      count.className = "cw-cart-count";
      count.setAttribute("aria-hidden", "true");
      cartLink.append(count);
    }

    if (count) {
      count.textContent = String(cart.item_count);
      count.hidden = cart.item_count === 0;
    }

    document.dispatchEvent(new CustomEvent("cw:cart:updated", { detail: cart }));
  };

  const submitProductForm = async (event, form) => {
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const status =
      form.querySelector("[data-product-status]") ||
      form.querySelector(".cw-product-card__form-status");

    if (button) {
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.classList.add("is-loading");
      button.dataset.loadingLabel = window.cwI18n?.("adding") || "Adding…";
    }

    try {
      const root = window.Shopify?.routes?.root || "/";
      const formData = new FormData(form);
      const quantityInput = form.closest("[data-cw-product]")?.querySelector(
        ".cw-product-quantity-block input[type=number]"
      );
      if (quantityInput) formData.set("quantity", quantityInput.value);

      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      const result = await response.json();

      if (!response.ok) throw new Error(result.description || window.cwI18n?.("add_error") || "This item could not be added.");

      if (status) status.textContent = window.cwI18n?.("added_to_bag") || "Added to your bag.";
      await refreshCartIndicator();
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        button.classList.remove("is-loading");
      }
    }
  };

  const initQuickAdd = (scope = document) => {
    scope.querySelectorAll(SELECTORS.quickAdd).forEach((form) => {
      if (form.dataset.initialized === "true") return;
      form.addEventListener("submit", (event) => submitProductForm(event, form));
      form.dataset.initialized = "true";
    });
  };

  /* --------------------------------------------------------------------------
   * Collection drawer and sort
   * -------------------------------------------------------------------------- */

  const initCollection = (collectionRoot) => {
    if (collectionRoot.dataset.initialized === "true") return;

    const panel = collectionRoot.querySelector("[data-filter-panel]");
    const toggle = collectionRoot.querySelector("[data-filter-toggle]");
    const close = collectionRoot.querySelector("[data-filter-close]");
    const scrim = collectionRoot.querySelector("[data-filter-scrim]");
    const sort = collectionRoot.querySelector("[data-sort-select]");
    const desktopQuery = window.matchMedia("(min-width: 990px)");

    const setDrawer = (open) => {
      if (!panel || !toggle || !scrim) return;
      panel.classList.toggle("is-open", open);
      scrim.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", String(open));
      document.documentElement.classList.toggle("cw-filter-drawer-open", open);
      panel.inert = !open && !desktopQuery.matches;
      panel.setAttribute("aria-hidden", String(!open && !desktopQuery.matches));

      if (open) {
        panel.querySelector("button, input, select, a")?.focus();
      } else if (!desktopQuery.matches) {
        toggle.focus();
      }
    };

    toggle?.addEventListener("click", () => setDrawer(true));
    close?.addEventListener("click", () => setDrawer(false));
    scrim?.addEventListener("click", () => setDrawer(false));
    sort?.addEventListener("change", () => sort.form?.submit());
    desktopQuery.addEventListener("change", () => setDrawer(false));

    collectionRoot.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && panel?.classList.contains("is-open")) setDrawer(false);
      if (event.key !== "Tab" || !panel?.classList.contains("is-open")) return;

      const focusable = [
        ...panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
      ];
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (panel) {
      panel.inert = !desktopQuery.matches;
      panel.setAttribute("aria-hidden", String(!desktopQuery.matches));
    }

    collectionRoot.dataset.initialized = "true";
  };

  /* --------------------------------------------------------------------------
   * Search input affordances
   * -------------------------------------------------------------------------- */

  const initSearch = (searchRoot) => {
    if (searchRoot.dataset.initialized === "true") return;

    const input = searchRoot.querySelector("[data-search-input]");
    const clear = searchRoot.querySelector("[data-search-clear]");

    if (!input || !clear) return;

    const updateClear = () => clear.classList.toggle("is-visible", input.value.length > 0);

    input.addEventListener("input", updateClear);
    clear.addEventListener("click", () => {
      input.value = "";
      updateClear();
      input.focus();
    });

    updateClear();
    searchRoot.dataset.initialized = "true";
  };

  /* --------------------------------------------------------------------------
   * Cart page line item updates
   * -------------------------------------------------------------------------- */

  const initCart = (cartRoot) => {
    if (cartRoot.dataset.initialized === "true") return;
    initQuantities(cartRoot);

    const form = cartRoot.querySelector("[data-cart-form]");
    const updateButton = form?.querySelector('[name="update"]');
    let updateTimer;

    if (form) {
      const scheduleCartUpdate = () => {
        window.clearTimeout(updateTimer);
        updateTimer = window.setTimeout(() => {
          if (typeof form.requestSubmit === "function" && updateButton) {
            form.requestSubmit(updateButton);
          } else {
            form.submit();
          }
        }, 350);
      };

      form.querySelectorAll("[data-cart-quantity]").forEach((input) => {
        input.addEventListener("change", scheduleCartUpdate);
      });
    }

    cartRoot.dataset.initialized = "true";
  };

  /* --------------------------------------------------------------------------
   * Initialization and Theme Editor support
   * -------------------------------------------------------------------------- */

  const initialize = (scope = document) => {
    initQuantities(scope);
    initQuickAdd(scope);
    scope.querySelectorAll(SELECTORS.product).forEach(initProduct);
    scope.querySelectorAll(SELECTORS.collection).forEach(initCollection);
    scope.querySelectorAll(SELECTORS.search).forEach(initSearch);
    scope.querySelectorAll(SELECTORS.cart).forEach(initCart);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  // Handle Theme Editor section reload idempotently
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target) {
      event.target.removeAttribute("data-initialized");
      event.target.querySelectorAll("[data-initialized]").forEach((el) => el.removeAttribute("data-initialized"));
      initialize(event.target);
    }
  });

  document.addEventListener("shopify:section:select", (event) => {
    if (event.target) {
      initialize(event.target);
    }
  });
})();
